import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import useLanguage from "../../hooks/useLanguage";
import useContent from "../../hooks/useContent";
import useUserData from "../../hooks/useUserData";
import useToaster from "../../hooks/useToaster";

export default function MyProfile({ open, onClose }) {
  const [
    {
      pages: { profile: text },
    },
  ] = useLanguage();
  const { updateMe, changeMyPassword } = useContent();
  const { addToaster } = useToaster();
  const user = useUserData();

  const [name, setName] = useState(user?.name || "");
  const [pw, setPw] = useState({ current: "", next: "" });
  const [savingName, setSavingName] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setPw({ current: "", next: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const persistUserdata = (patch) => {
    try {
      const raw = window.localStorage.getItem("userdata");
      const next = raw ? { ...JSON.parse(raw), ...patch } : patch;
      window.localStorage.setItem("userdata", JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  const handleSaveName = async () => {
    if (!name.trim() || name.trim() === user?.name) return;
    setSavingName(true);
    try {
      const updated = await updateMe({ name: name.trim() });
      persistUserdata({
        name: updated?.name ?? name.trim(),
        nickname: updated?.name ?? name.trim(),
        initials: (updated?.name ?? name.trim())
          .split(" ")
          .map((n) => n[0])
          .join(""),
      });
      addToaster({ message: text.toaster.saved, severity: "success" });
    } catch {
      // toaster already raised by safeFetch
    } finally {
      setSavingName(false);
    }
  };

  const handleChangePassword = async () => {
    if (!pw.current || !pw.next) return;
    setSavingPw(true);
    try {
      await changeMyPassword({
        currentPassword: pw.current,
        newPassword: pw.next,
      });
      setPw({ current: "", next: "" });
      addToaster({ message: text.toaster.passwordChanged, severity: "success" });
    } catch {
      // toaster already raised by safeFetch
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{text.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {text.subtitle}
          </Typography>

          <Stack spacing={1.5}>
            <TextField
              label={text.fields.name}
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              fullWidth
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleSaveName}
              disabled={savingName || !name.trim() || name.trim() === user?.name}
            >
              {text.save}
            </Button>
          </Stack>

          <TextField
            label={text.fields.email}
            value={user?.email || ""}
            size="small"
            fullWidth
            disabled
          />
          <TextField
            label={text.fields.role}
            value={user?.role || ""}
            size="small"
            fullWidth
            disabled
          />
          <TextField
            label={text.fields.group}
            value={user?.group || ""}
            size="small"
            fullWidth
            disabled
          />

          <Divider />

          <Typography variant="subtitle2">
            {text.changePassword.title}
          </Typography>
          <TextField
            label={text.changePassword.current}
            type="password"
            value={pw.current}
            onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
            size="small"
            fullWidth
          />
          <TextField
            label={text.changePassword.next}
            type="password"
            value={pw.next}
            onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
            size="small"
            fullWidth
          />
          <Button
            variant="outlined"
            size="small"
            color="warning"
            onClick={handleChangePassword}
            disabled={savingPw || !pw.current || !pw.next}
          >
            {text.changePassword.confirm}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {text.close}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
